import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import Page from "components/Page/Page";
import Card from "components/Card/Card";
import Button from "components/Button/Regular";
import { useActiveOrg } from "context/ActiveOrgContext";
import vgToast from "utils/toast";
import { ClientRow, getClientsList } from "services/lists.service";
import {
  ARRANGEMENT_STATUS_LABEL,
  Arrangement,
  ArrangementStatus,
  ArrangementStep,
  deleteStep,
  readArrangement,
  saveArrangement,
  saveStep,
} from "services/arrangements.service";

const inputClass = "rounded-md border border-slate-300 px-3 py-2 text-sm";

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** One arrangement: who has died, the family, where it stands, and the running order of the day. */
const ArrangementDetails = () => {
  const { id } = useParams();
  const arrangementId = Number(id);
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [arrangement, setArrangement] = React.useState<Arrangement | null>(null);
  const [families, setFamilies] = React.useState<ClientRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [stepTitle, setStepTitle] = React.useState("");
  const [stepTime, setStepTime] = React.useState("");
  const [stepPlace, setStepPlace] = React.useState("");

  React.useEffect(() => {
    if (orgId <= 0 || !arrangementId) return;
    let cancelled = false;
    (async () => {
      const token = await getAccessTokenSilently();
      const [{ data, error: readError }, clients] = await Promise.all([
        readArrangement(token, orgId, arrangementId),
        getClientsList(token, orgId),
      ]);
      if (cancelled) return;
      if (readError) setError(readError.message);
      else setArrangement(data);
      setFamilies(clients.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, arrangementId, getAccessTokenSilently]);

  const update = (changes: Partial<Arrangement>) => arrangement && setArrangement({ ...arrangement, ...changes });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!arrangement) return;
    setSaving(true);
    const token = await getAccessTokenSilently();
    const { data, error: saveError } = await saveArrangement(token, arrangement);
    setSaving(false);
    if (saveError) {
      vgToast.error(saveError.message);
      return;
    }
    if (data) setArrangement(data);
    vgToast.success("Arrangement saved.");
  };

  const changeStep = async (step: ArrangementStep, remove = false) => {
    if (!arrangement) return;
    const token = await getAccessTokenSilently();
    const { data, error: stepError } = remove
      ? await deleteStep(token, orgId, arrangement.arrangementId, step)
      : await saveStep(token, orgId, arrangement.arrangementId, step);
    if (stepError) {
      vgToast.error(stepError.message);
      return false;
    }
    if (data) setArrangement(data);
    return true;
  };

  const addStep = async (e: React.FormEvent) => {
    e.preventDefault();
    const added = await changeStep({
      stepId: 0,
      arrangementId,
      startsAt: stepTime,
      title: stepTitle,
      place: stepPlace,
      isDone: false,
    });
    if (added) {
      setStepTitle("");
      setStepPlace("");
    }
  };

  if (error) {
    return (
      <Page title="Arrangement">
        <p className="text-sm text-red-500">{error}</p>
      </Page>
    );
  }
  if (!arrangement) {
    return (
      <Page title="Arrangement">
        <p className="text-sm text-slate-500">Loading…</p>
      </Page>
    );
  }

  return (
    <Page title={arrangement.deceasedFullName} subTitle={ARRANGEMENT_STATUS_LABEL[arrangement.status]}>
      <div className="flex flex-col gap-5">
        <Card title="Details">
          <form onSubmit={save} className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 sm:col-span-2">
              Name of the person who has died
              <input className={inputClass} value={arrangement.deceasedFullName} onChange={(e) => update({ deceasedFullName: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Date of birth
              <input className={inputClass} type="date" value={arrangement.dateOfBirth} onChange={(e) => update({ dateOfBirth: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Date of death
              <input className={inputClass} type="date" value={arrangement.dateOfDeath} onChange={(e) => update({ dateOfDeath: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Place of death
              <input className={inputClass} value={arrangement.placeOfDeath} onChange={(e) => update({ placeOfDeath: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Funeral date
              <input className={inputClass} type="date" value={arrangement.funeralDate} onChange={(e) => update({ funeralDate: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Family contact
              <select
                className={inputClass}
                value={arrangement.familyContactId ?? ""}
                onChange={(e) => update({ familyContactId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Not chosen yet</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.email ? ` (${f.email})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Status
              <select
                className={inputClass}
                value={arrangement.status}
                onChange={(e) => update({ status: Number(e.target.value) as ArrangementStatus })}
              >
                {(Object.keys(ARRANGEMENT_STATUS_LABEL) as unknown as ArrangementStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {ARRANGEMENT_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 sm:col-span-2">
              Notes
              <textarea className={inputClass} rows={4} value={arrangement.notes} onChange={(e) => update({ notes: e.target.value })} />
            </label>
            <div className="sm:col-span-2">
              <Button color="Primary" type="submit" disabled={saving}>
                <span>{saving ? "Saving…" : "Save"}</span>
              </Button>
            </div>
          </form>
        </Card>

        <Card title="The day" subTitle="The running order, shared with everyone helping.">
          <div className="p-6 flex flex-col gap-4">
            {arrangement.steps.length === 0 && <p className="text-sm text-slate-500">No steps yet.</p>}
            {arrangement.steps.length > 0 && (
              <ol className="flex flex-col gap-2">
                {arrangement.steps.map((s) => (
                  <li key={s.stepId} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      aria-label={`${s.title} done`}
                      className="h-4 w-4"
                      checked={s.isDone}
                      onChange={(e) => changeStep({ ...s, isDone: e.target.checked })}
                    />
                    <span className="w-44 text-xs text-slate-500">{timeLabel(s.startsAt)}</span>
                    <span className={`grow text-sm ${s.isDone ? "text-slate-400 line-through" : "text-slate-800"}`}>
                      {s.title}
                      {s.place && <span className="text-slate-500"> · {s.place}</span>}
                    </span>
                    <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => changeStep(s, true)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ol>
            )}
            <form onSubmit={addStep} className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-end">
              <label className="flex flex-col gap-1.5 text-sm text-slate-600">
                Time
                <input className={inputClass} type="datetime-local" value={stepTime} onChange={(e) => setStepTime(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1.5 text-sm text-slate-600">
                What happens
                <input className={inputClass} value={stepTitle} onChange={(e) => setStepTitle(e.target.value)} placeholder="Service at the chapel" />
              </label>
              <label className="flex flex-col gap-1.5 text-sm text-slate-600">
                Where
                <input className={inputClass} value={stepPlace} onChange={(e) => setStepPlace(e.target.value)} />
              </label>
              <Button color="Secondary" type="submit">
                <span>Add step</span>
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </Page>
  );
};

export default ArrangementDetails;
